import React, { useState } from 'react';
import { Avatar, Button, TextField, Box, Typography, FormControl, FormHelperText, InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

export default function ChangeDefaultPasswordForm({ onSubmit }) {
  const [inputs, setInputs] = useState({
    newPassword: '',
    confirmPassword: '',
    showPassword: false,
    passwordError: false,
    passwordsMatch: true,
    errorMessage: []
  });

  const passwordCriteria = [
    { test: password => password.length >= 8, message: "Minimum length: 8 characters" },
    { test: password => /[A-Z]/.test(password), message: "Contains at least 1 uppercase letter" },
    { test: password => /[a-z]/.test(password), message: "Contains at least 1 lowercase letter" },
    { test: password => /[0-9]/.test(password), message: "Contains at least 1 number" },
    { test: password => /[\^$*.{}[\]()?"!@#%&/\\,><':;|_~`+=-]/.test(password), message: "Contains at least 1 special character" },
  ];

  // Function to update state based on input changes
  function handleInputChanges(e) {
    const { name, value } = e.target;
    const updatedInputs = { ...inputs, [name]: value };

    // Check password criteria
    updatedInputs.errorMessage = passwordCriteria.map(({ test, message }) => ({
      success: test(updatedInputs.newPassword),
      message,
    }));

    // Check if passwords match
    updatedInputs.passwordsMatch = updatedInputs.newPassword === updatedInputs.confirmPassword;
    updatedInputs.passwordError = !updatedInputs.passwordsMatch || updatedInputs.errorMessage.some(error => !error.success);

    setInputs(updatedInputs);
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!inputs.passwordError) {
      onSubmit(event);
    }
  };

  const handleClickShowPassword = () => {
    setInputs({ ...inputs, showPassword: !inputs.showPassword });
  };

  return (
    <Box sx={{ marginTop: 8, display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
        <LockOutlinedIcon />
      </Avatar>
      <Typography component="h1" variant="h5">Change Password</Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <FormControl error={inputs.passwordError} fullWidth>
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="New Password"
            type={inputs.showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={inputs.newPassword}
            onChange={handleInputChanges}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} edge="end">
                    {inputs.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type={inputs.showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={inputs.confirmPassword}
            onChange={handleInputChanges}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} edge="end">
                    {inputs.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {inputs.errorMessage.map(({ success, message }, index) => (
            <FormHelperText key={index} style={{ color: success ? "green" : "red" }}>
              {typeof message === 'object' ? message.message : message}
            </FormHelperText>
          ))}
          {!inputs.passwordsMatch && <FormHelperText style={{ color: "red" }}>
            Passwords do not match
          </FormHelperText>}
        </FormControl>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={inputs.passwordError}
        >
          Change Password
        </Button>
      </Box>
    </Box>
  );
}